import {IBasicUniversePointer} from "/@lib@/core/universe.js";
import {IAuthenticatedEnv, IReg, IRemoteAuth, REG} from "/@lib@/commons/registry.js";
import {OButtonActionsInit} from "/@back@/commons/widgets/buttons.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IEndPoint} from "/@lib@/commons/io/io.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";
import {UserSelfResponse} from "/@lib@/core/userSelf.js";
import {MsgLabel} from "/@back@/commons/basis.js";
import {LANG} from "/@lib@/commons/lang.js";


/**
 * Remote auth SAML
 */

export interface ORemoteAuthConfig {
	authMethod: string
	label: string
	iconUrl?: string // URL résolue dynamiquement à partir de 'icon'

	/** Accés au service SAML (non authentifié). */
	remoteSvcUrl?: IEndPoint;

	/** Accés au service SAML (authentifié). */
	remoteAuthSvcUrl?: IEndPoint;
}

interface JRemoteAuthRequest {
	authMethod: string,
	path: string,
	account?: string
}



interface JRemoteAuthSamlIdpLogin {
	binding: string,
	url: string,
	message: string,
	state?: string
}


export enum ESamlBinding {
	getQs = "urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect",
	postForm = "urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
}


export class RemoteAuthSaml<C extends IBasicUniversePointer & IAuthenticatedEnv> implements IRemoteAuth<JRemoteAuthSamlLoginRespMsg> {
	constructor(protected config: ORemoteAuthConfig) {}

	get authMethod() {return this.config?.authMethod}

	configAuthSrv(authenticatedExecFrameUrl: IEndPoint, publicExecFrameUrl: IEndPoint) {
		if (!this.config.remoteSvcUrl) this.config.remoteSvcUrl = publicExecFrameUrl.resolve("u/saml");
		if (!this.config.remoteAuthSvcUrl) this.config.remoteAuthSvcUrl = authenticatedExecFrameUrl.resolve("u/saml");
	}

	redrawLogin(reg: IReg<IBasicUniversePointer & IAuthenticatedEnv>, lastRemoteLoginAttempt?: JRemoteAuthSamlLoginRespMsg) {
		const resp = <div/>
		const name = this.config.label;
		resp.appendChild(<c-button î={{label: `Se connecter via '${name}'...`, icon: this.config.iconUrl, role: 'button', uiContext: 'dialog', skinOver: 'remote-auth-btn'} as OButtonActionsInit<any>}
															 onclick={this.doLogin.bind(this)}/>);
		if (lastRemoteLoginAttempt?.msg) {
			const msg = resp.appendChild(<MsgLabel/>) as MsgLabel;
			switch (lastRemoteLoginAttempt.msg) {
			case "accountNotFound" :
				msg.setCustomMsg(LANG.formatStr("Le compte '%s' n'existe pas.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break
			case "accountDisabled" :
				msg.setCustomMsg(LANG.formatStr("Le compte '%s' est désactivé.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break
			case "accountNotAuthorized" :
				msg.setCustomMsg(LANG.formatStr("Le service d'authentification distant ne vous autorise pas à vous connecter.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break
			case "tooOldToken" :
				msg.setCustomMsg(LANG.formatStr("L'autorisation du système de connexion distant est trop ancienne. Veuillez réessayer.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break
			case "otherFailure" :
				msg.setCustomMsg(LANG.formatStr("Une erreur est survenue, impossible de poursuivre.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break;
			}
		}
		return resp;
	}

	async logout(reg: IReg<any>): Promise<boolean> {
		const logout = await this.fetchAuthenticatedSaml({"authMethod":this.authMethod, path:window.location.toString()}, "?cdaction=Logout");
		if(logout.message !== null){
			this.redirect(logout);
			return false;
		}
		else return true;

	}

	private async doLogin() {
		const login = await this.fetchPublicSaml({"authMethod":this.authMethod, path:window.location.toString()}, "?cdaction=Login");
		this.redirect(login);
	}

	private async fetchPublicSaml(bodyProps: JRemoteAuthRequest, cdaction:string): Promise<JRemoteAuthSamlIdpLogin> {
		const initReq = {method: 'POST'} as RequestInit;
		const body = initReq.body = new FormData();
		body.append("userProps", CDM.stringify(bodyProps));
		const resp = await this.config.remoteSvcUrl.fetchJson<UserSelfResponse<JRemoteAuthSamlIdpLogin>>(cdaction, initReq);
		return resp.result;
	}

	private async fetchAuthenticatedSaml(bodyProps:JRemoteAuthRequest, cdaction:string): Promise<JRemoteAuthSamlIdpLogin> {
		const initReq = {method: 'POST'} as RequestInit;
		const body = initReq.body = new FormData();
		body.append("userProps", CDM.stringify(bodyProps));
		const resp = await this.config.remoteAuthSvcUrl.fetchJson<UserSelfResponse<JRemoteAuthSamlIdpLogin>>(cdaction, initReq);
		return resp.result;
	}

	private redirect(resp: JRemoteAuthSamlIdpLogin) {
		if (resp.binding == ESamlBinding.getQs) {
			var url = resp.url;
			url += url.indexOf("?") != -1 ? "&" : "?";
			url += "SAMLRequest=" + encodeURIComponent(resp.message);
			url += "&RelayState=" + encodeURIComponent(resp.state);
			window.open(url, "_self");
		} else if (resp.binding == ESamlBinding.postForm) {
			const form = document.createElement("form");
			const samlrequest = document.createElement("input");
			const state = document.createElement("input");

			form.method = "POST";
			form.action = resp.url;

			samlrequest.name = "SAMLRequest";
			samlrequest.value = resp.message;
			form.appendChild(samlrequest);

			state.name = "RelayState";
			state.value = resp.state;
			form.appendChild(state);

			document.body.appendChild(form);
			form.submit();
		} else console.error("unknown SAML binding " + resp.binding);
	}

}


export interface JRemoteAuthSamlLoginRespMsg {
	msg: 'accountNotFound'|'accountDisabled'|'accountNotAuthorized'|'tooOldToken'|'otherFailure'
	msgVars?: string[],
}


interface JRemoteAuthOAuthLogin {
	authorizationUrl?:string
	sloUrl?:string
}

export class RemoteAuthOAuth<C extends IBasicUniversePointer & IAuthenticatedEnv> implements IRemoteAuth<JRemoteAuthSamlLoginRespMsg> {
	constructor(protected config: ORemoteAuthConfig) {}

	get authMethod() {return this.config?.authMethod}

	configAuthSrv(authenticatedExecFrameUrl: IEndPoint, publicExecFrameUrl: IEndPoint) {
		if (!this.config.remoteSvcUrl) this.config.remoteSvcUrl = publicExecFrameUrl.resolve("u/oauth");
		if (!this.config.remoteAuthSvcUrl) this.config.remoteAuthSvcUrl = authenticatedExecFrameUrl.resolve("u/oauth");
	}

	redrawLogin(reg: IReg<IBasicUniversePointer & IAuthenticatedEnv>, lastRemoteLoginAttempt?: JRemoteAuthSamlLoginRespMsg) {
		const resp = <div/>
		const name = this.config.label;
		resp.appendChild(<c-button î={{label: `Se connecter via '${name}'...`, icon: this.config.iconUrl, role: 'button', uiContext: 'dialog', skinOver: 'remote-auth-btn'} as OButtonActionsInit<any>}
															 onclick={this.doLogin.bind(this)}/>);
		if (lastRemoteLoginAttempt?.msg) {
			const msg = resp.appendChild(<MsgLabel/>) as MsgLabel;
			switch (lastRemoteLoginAttempt.msg) {
			case "accountNotFound" :
				msg.setCustomMsg(LANG.formatStr("Le compte '%s' n'existe pas.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break
			case "accountDisabled" :
				msg.setCustomMsg(LANG.formatStr("Le compte '%s' est désactivé.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break
			case "otherFailure" :
				msg.setCustomMsg(LANG.formatStr("Une erreur est survenue, impossible de poursuivre.", lastRemoteLoginAttempt.msgVars || []), "warning");
				break;
			}
		}
		return resp;
	}

	async logout(reg: IReg<any>): Promise<boolean> {
		const logout = await this.fetchAuthenticatedOAuth({"authMethod":this.authMethod, path:window.location.toString()}, "?cdaction=Logout");
		if(logout.sloUrl !== null){
			window.open(logout.sloUrl, "_self")
		}
		else return true;
	}

	private async doLogin() {
		const login = await this.fetchPublicOAuth({"authMethod":this.authMethod, path:window.location.toString()}, "?cdaction=Login");
		window.open(login.authorizationUrl, "_self")
	}

	private async fetchPublicOAuth(bodyProps: JRemoteAuthRequest, cdaction:string): Promise<JRemoteAuthOAuthLogin> {
		const initReq = {method: 'POST'} as RequestInit;
		const body = initReq.body = new FormData();
		body.append("userProps", CDM.stringify(bodyProps));
		const resp = await this.config.remoteSvcUrl.fetchJson<UserSelfResponse<JRemoteAuthOAuthLogin>>(cdaction, initReq);
		return resp.result;
	}

	private async fetchAuthenticatedOAuth(bodyProps:JRemoteAuthRequest, cdaction:string): Promise<JRemoteAuthOAuthLogin> {
		const initReq = {method: 'POST'} as RequestInit;
		const body = initReq.body = new FormData();
		body.append("userProps", CDM.stringify(bodyProps));
		const resp = await this.config.remoteAuthSvcUrl.fetchJson<UserSelfResponse<JRemoteAuthOAuthLogin>>(cdaction, initReq);
		return resp.result;
	}

}
REG.reg.registerSkin('remote-auth-btn', 1, /* language=CSS */ `
	:host {
		font-weight: bold;
		padding-block: 0.4rem;
		color: var(--inv-color);
		background-color: var(--inv-bgcolor);
	}

  :host(:hover:not([disabled])) {
	  background-image: linear-gradient(var(--inv-pressed-bgcolor), var(--inv-pressed-bgcolor));
	  background-color: var(--inv-bgcolor);
  }

`);